// Slack-style reactor-name formatting for the reaction tooltip. Pulled
// out of MessageReactions.tsx so the grammar rules (You first, "and", the
// "N others" cap) stay testable without spinning up a Radix tooltip.

const MAX_NAMES_BEFORE_OVERFLOW = 4;

interface FormatReactorListOpts {
  /** All reactors in reaction-time order (the order `groupReactionsByEmoji`
   * pushes them). May contain `viewerName` anywhere. */
  users: readonly string[];
  /** The viewer's display name, or null when the viewer didn't react. When
   * present and found in `users`, that entry is replaced with "You" and
   * hoisted to the front of the sentence. */
  viewerName: string | null;
}

/**
 * Format a list of reactor display names into a single human sentence,
 * Slack-style:
 *
 *   ["Maya"]                                         → "Maya"
 *   ["Maya", "Felix"]                                → "Maya and Felix"
 *   ["Maya", "Felix", "Priya"]                       → "Maya, Felix, and Priya"
 *   ["Maya", "Felix", "Priya", "Jonas"]              → "Maya, Felix, Priya, and Jonas"
 *   ["Maya", "Felix", "Priya", "Jonas", "Leo"]       → "Maya, Felix, Priya, Jonas, and 1 other"
 *   ["Maya", "Felix", "Priya", "Jonas", "Leo", "Ada"]→ "Maya, Felix, Priya, Jonas, and 2 others"
 *
 * When `viewerName` matches an entry, that entry becomes "You" and is
 * hoisted to the front: `formatReactorList({ users: ["Maya","You-display"],
 * viewerName: "You-display" })` → "You and Maya".
 */
export function formatReactorList({
  users,
  viewerName,
}: FormatReactorListOpts): string {
  if (users.length === 0) return "";

  // Hoist the viewer (if present) to the front and rename to "You".
  let ordered: string[];
  if (viewerName !== null) {
    const idx = users.indexOf(viewerName);
    if (idx >= 0) {
      ordered = ["You", ...users.slice(0, idx), ...users.slice(idx + 1)];
    } else {
      // viewerName provided but not found — defensive fall-through. The
      // caller usually only sets viewerName when it's known to be in the
      // list; missing here means a stale display-name lookup.
      ordered = [...users];
    }
  } else {
    ordered = [...users];
  }

  if (ordered.length === 1) return ordered[0];
  if (ordered.length === 2) return `${ordered[0]} and ${ordered[1]}`;

  if (ordered.length <= MAX_NAMES_BEFORE_OVERFLOW) {
    const head = ordered.slice(0, -1).join(", ");
    return `${head}, and ${ordered[ordered.length - 1]}`;
  }
  const visible = ordered.slice(0, MAX_NAMES_BEFORE_OVERFLOW);
  const overflow = ordered.length - visible.length;
  return `${visible.join(", ")}, and ${overflow} other${overflow === 1 ? "" : "s"}`;
}
